import { createContext, useContext, useEffect, useMemo, useState } from "react"
import { useDispatch } from "react-redux"
import { AxiosError, isAxiosError } from "axios"

import { USER_ACCOUNT_LOADING_STATUS } from "../Default/common/constant"

import { createNonVerifiedUserAccount, createVerifiedUserAccount, isVerified } from "./utils"
import {
    ClientContextHandlers,
    ClientContextValue,
    ClientProviderProps,
    UserAccount as UserAccountT,
    VerifiedUserAccount,
    VerifyRequestResponse,
} from "./interfaces"

import {
    isBookingDetailsPage,
    isBookingPage,
    isChat,
    isClientAccountPage,
    isListPage,
    isPaymentPage,
    isProfilePage,
    isPsychologistCollectionPage,
    isRootPage,
    isSearchPage,
    isSurveyPage,
    isSurveyResultsPage,
} from "app/AppRouter/utils"
import { SentryWrapper } from "utils/ThirdParty/Sentry/SentryWrapper"
import { axiosClient, httpClient } from "utils/infrastructure/httpClient"
import { isBrowser } from "utils/infrastructure/ssg-utils"
import { gaSetUserProperty } from "utils/analytics/google-analytics"
import { UserAccount } from "utils/UserAccount"
import { setClientHash } from "utils/store/slices/user"
import { AppDispatch } from "utils/store/store"

const ClientContext = createContext<[ClientContextValue, ClientContextHandlers]>([
    {
        loading: USER_ACCOUNT_LOADING_STATUS.IDLE,
        isVerified: false,
        userAccount: createNonVerifiedUserAccount(),
    },
    { reloadClient: async () => undefined, changeAccount: () => undefined },
])

export function ClientProvider({ children }: ClientProviderProps) {
    const dispatch = useDispatch<AppDispatch>()
    const [loadingStatus, setLoadingStatus] = useState<USER_ACCOUNT_LOADING_STATUS>(
        USER_ACCOUNT_LOADING_STATUS.IDLE
    )
    const [userAccount, setUserAccount] = useState<UserAccountT>(createNonVerifiedUserAccount())
    const verified = isVerified(userAccount)

    const handlers = useMemo(() => {
        return {
            reloadClient: async () => {
                if (!isBrowser()) return

                setLoadingStatus(USER_ACCOUNT_LOADING_STATUS.LOADING)

                try {
                    const data = await httpClient.get<VerifyRequestResponse>(`client/auth2/verify`)

                    if (!data) {
                        setUserAccount(createNonVerifiedUserAccount())
                        return
                    }

                    dispatch(setClientHash(data.hash_id))
                    setUserAccount(createVerifiedUserAccount(data))
                } catch {
                    setUserAccount(createNonVerifiedUserAccount())
                } finally {
                    setLoadingStatus(USER_ACCOUNT_LOADING_STATUS.READY)
                }
            },
            changeAccount: (value: Partial<UserAccountT>) => {
                if (!value && !isVerified(userAccount)) {
                    return
                }

                setUserAccount((prev) => {
                    return {
                        ...prev,
                        ...value,
                    } as VerifiedUserAccount
                })
            },
        }
    }, [])

    useEffect(() => {
        handlers.reloadClient()
        let isActive = true
        const excludedUrls = ["/api/pricing/deposit/configurations"]

        const interceptorId = axiosClient.interceptors.response.use(
            (response) => response,
            (error: Error | AxiosError) => {
                if (isActive && isAxiosError(error) && error.response?.status === 401) {
                    const requestUrl = error.config?.url || ""

                    const isExcluded = excludedUrls.some((excludedUrl) =>
                        requestUrl.endsWith(excludedUrl)
                    )

                    if (!isExcluded) {
                        setUserAccount(createNonVerifiedUserAccount())
                    }
                }

                return Promise.reject(error)
            }
        )

        return () => {
            isActive = false
            axiosClient.interceptors.response.eject(interceptorId)
        }
    }, [])

    useEffect(() => {
        if (verified) {
            const remove = SentryWrapper.getInstance().saveUserToScope("client", userAccount.email)
            UserAccount.storeSignIn({
                phone: userAccount.phone,
                email: userAccount.email,
                name: userAccount.name,
                company_inn: userAccount.company_inn,
                company_ooo: userAccount.company_ooo,
                company_ogrn: userAccount.company_ogrn,
                company_address: userAccount.company_address,
                psychologists: userAccount.psychologists,
            })
            gaSetUserProperty("email_provider", () => userAccount.email.split("@")[1])
            UserAccount.runSignUpQueue()
            return remove
        }
    }, [verified])

    return (
        <ClientContext.Provider
            value={[
                {
                    loading: loadingStatus,
                    isVerified: verified,
                    userAccount,
                },
                handlers,
            ]}
        >
            {children}
        </ClientContext.Provider>
    )
}

ClientProvider.isApplicable = () =>
    isProfilePage() ||
    isSurveyResultsPage() ||
    isSurveyPage() ||
    isListPage() ||
    isSearchPage() ||
    isBookingPage() ||
    isClientAccountPage() ||
    isPsychologistCollectionPage() ||
    isPaymentPage() ||
    isBookingDetailsPage() ||
    isChat() ||
    isRootPage()

export function useClientContext() {
    return useContext(ClientContext)
}
